import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";

import moment from "moment";
import { MdDeleteSweep } from "react-icons/md";
import { TbCloudDownload } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import Button from "../../Components/atoms/Button";
import SideBarSkeleton from "../../Components/atoms/SideBarSkeleton";
import SectionHeader from "../../Components/molecules/SectionHeader";
import Table from "../../Components/organisms/Table";
import { RECORDS_LIMIT } from "../../developmentContent/constants";
import { Delete, Get } from "../../interceptor/axiosInterceptor";
import useDebounce from "../../resources/customHooks/useDebounce";
import { downloadFile } from "../../resources/utils/downloadFIle";
import { mergeClass } from "../../resources/utils/helper";
import classes from "./Newsletter.module.css";
import RenderToast from "../../Components/atoms/RenderToast/RenderToast";
import AreSureModal from "../../Components/molecules/AreSureModal";

export default function Newsletter() {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const debounceSearch = useDebounce(search);
  const [selectedItem, setSelectedItem] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);

  const getData = async ({ pg = page, _search = debounceSearch }) => {
    setLoading("loadings");
    const params = {
      page: pg,
      search: _search,
      limit: RECORDS_LIMIT,
    };

    const queryParams = new URLSearchParams(params)?.toString();
    const url = "admin/newsletter";
    const { response } = await Get({
      route: `${url}?${queryParams}`,
    });

    if (response) {
      setData(response?.data?.data);
      setPage(pg);
      setTotalRecords(response?.data?.totalRecords);
    }
    setLoading("");
  };

  // handleDownloadCsv
  const handleDownloadCsv = async () => {
    if (loading === "csv") return;
    setLoading("csv");
    const query = {
      search: debounceSearch,
    };
    const queryString = new URLSearchParams(query).toString();
    downloadFile({
      route: `admin/newsletter/download-csv?${queryString}`,
      fileName: "Newsletter",
      extension: ".csv",
    }).finally(() => {
      setLoading("");
    });
  };

  //handle delete
  const handleDelete = async (id) => {
    setLoading("deleting");

    const { response } = await Delete({
      route: `admin/newsletter/${id}`,
    });

    if (response) {
      RenderToast({
        type: "success",
        message: "Deleted successfully",
      });
      getData({ pg: page, _search: debounceSearch });
    }
    setLoading("");
  };

  useEffect(() => {
    setPage(1);
    getData({ pg: page, _search: debounceSearch });
  }, [debounceSearch]);

  const renderStatusBadge = (status, errorMsg, syncedAt) => {
    const isSynced = status === "synced" || (!status && syncedAt);
    const isFailed = status === "failed";

    if (isSynced) {
      return (
        <span
          className="badge bg-success-subtle text-success px-3 py-2 rounded-pill font-weight-bold"
          style={{ fontSize: "13px", border: "1px solid #198754" }}
          title={syncedAt ? `Synced at ${moment(syncedAt).format("DD-MM-YYYY HH:mm")}` : "Synced to Constant Contact"}
        >
          ✓ Synced to Constant Contact
        </span>
      );
    }

    if (isFailed) {
      return (
        <span
          className="badge bg-danger-subtle text-danger px-3 py-2 rounded-pill font-weight-bold"
          style={{ fontSize: "13px", border: "1px solid #dc3545", cursor: "pointer" }}
          title={errorMsg || "Failed to sync"}
        >
          ✕ Sync Failed
        </span>
      );
    }

    return (
      <span
        className="badge bg-warning-subtle text-warning-emphasis px-3 py-2 rounded-pill font-weight-bold"
        style={{ fontSize: "13px", border: "1px solid #ffc107" }}
        title="Pending sync"
      >
        ⏳ Pending
      </span>
    );
  };

  const tableHeader = [
    { id: "email", label: "Email", style: { width: "35%" } },
    { id: "createdAt", label: "Date", style: { width: "20%" } },
    {
      id: "constantContact",
      label: "Constant Contact",
      style: { width: "25%", textAlign: "center" },
      className: classes.tableHeadings,
      renderValue: (item) => renderStatusBadge(item?.constantContactStatus, item?.constantContactError, item?.syncedAt),
    },
    {
      id: "actions",
      style: { width: "20%", textAlign: "center" },
      className: classes.tableHeadings,
      renderValue: (item) => (
        <Button
          className={mergeClass("fs-16 fw-700", classes.actionButton)}
          variant="borderRed"
          label={
            selectedItem === item?._id && loading === "deleting"
              ? "Deleting..."
              : "Delete"
          }
          disabled={selectedItem === item?._id && loading === "deleting"}
          leftIcon={<MdDeleteSweep size={20} />}
          onClick={() => {
            setSelectedItem(item?._id);
            setShowModal(true);
          }}
        />
      ),
    },
  ];

  const tableBody = data?.map((item) => ({
    _id: item._id,
    email: item.email,
    createdAt: moment(item?.createdAt).format("DD-MM-YYYY"),
    constantContactStatus: item.constantContactStatus,
    constantContactError: item.constantContactError,
    syncedAt: item.syncedAt,
  }));

  return (
    <SideBarSkeleton>
      <Container fluid>
        <Row>
          <Col md={12}>
            <SectionHeader
              title="Newsletters"
              btnClass={classes.btnClass}
              showButton
              label={loading === "csv" ? "Downloading..." : "Download CSV"}
              disabled={loading === "csv"}
              leftIcon={<TbCloudDownload size={25} />}
              onClick={handleDownloadCsv}
              showSearch
              search={search}
              setSearch={setSearch}
            />
          </Col>
          <Col md={12}>
            <div className={classes.tableDiv}>
              <Table
                headings={tableHeader}
                tableData={tableBody}
                currentPage={page}
                totalRecords={totalRecords}
                hasPagination
                {...{ totalRecords, currentPage: page }}
                loading={loading === "loadings"}
                setCurrentPage={(p) => {
                  setPage(p);
                  getData({ pg: p, _search: debounceSearch });
                }}
              />
            </div>
          </Col>
        </Row>
      </Container>
      {showModal && (
        <AreSureModal
          show={showModal}
          setShow={setShowModal}
          message={"Are you sure you want to delete this newsletter?"}
          onYes={() => {
            handleDelete(selectedItem);
            setShowModal(false);
          }}
          showReason={false}
          isLoading={loading === "deleting"}
        />
      )}
    </SideBarSkeleton>
  );
}
